import { useAppContext, usePermissions, useAccessLevel, useFeatureFlags } from 'core/app/AppContext';
import { fetchObjectFromServer } from 'core/ui/utils/fetchFromServer';
import { User } from 'entities/user';
import React, { createContext, useContext, useEffect, useState } from 'react';

const UserContext = createContext<Context>(null);

interface Context {
  didCancel: boolean;
  isLoading: boolean;
  isLoaded: boolean;
  users: User[];
  getUser: (userId: string) => User;
}

export const UserProvider: React.FC = props => {
  const { isAppStateLoading } = useAppContext();
  const [didCancel, setDidCancel] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const { hasPermission } = usePermissions();
  const { accessLevel } = useAccessLevel();
  const { flagEnabled } = useFeatureFlags();
  const getUser = (userId: string) => users.find(user => user.sub === userId) ?? null;

  useEffect(() => {
    if (isAppStateLoading || isLoading || isLoaded || !hasPermission('ManageUsers') || accessLevel !== 'SaleCycle') {
      return;
    }

    if (!isLoading && !isLoaded) {
      const fetchAllUsers = async () => {
        setIsLoading(true);

        try {
          const users = await fetchObjectFromServer<User[]>('/users');
          setUsers(users.entity);
        } finally {
          setIsLoaded(true);
          setIsLoading(false);
        }
      };

      fetchAllUsers();
    }

    return () => {
      setDidCancel(true);
    };
  }, [isAppStateLoading, flagEnabled, hasPermission, isLoading, isLoaded, accessLevel]);

  return <UserContext.Provider value={{ didCancel, isLoading, isLoaded, users, getUser }} {...props} />;
};

export function useUserContext() {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error(`useUserContext must be used within a UserProvider`);
  }

  return {
    didCancel: context.didCancel,
    users: context.users,
    isLoaded: context.isLoaded,
    isLoading: context.isLoading,
    getUser: context.getUser,
  };
}
